import { useMutation, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import { codeKeys, useChildCodeIdsFromCache } from '../queries';
import { normalizeId } from '../../utils/idNormalization';
import useUpdateCodeField from './useUpdateCodeField';
import { useOptimisticPromise } from 'hooks/optimistic/useOptimisticPromise';

/**
 * Hook for updating a code with optimistic updates
 * @returns {Object} The mutation object with mutate function and state
 */
export function useUpdateCode() {
  return useUpdateCodeField(
    ({ codeId, ...codeAttributes }) => QualAPI.updateCode(codeId, codeAttributes).then(response => response.data),
    'Error updating code'
  );
}

/**
 * Hook for bulk updating code colors using the new bulk endpoint
 * With proper optimistic updates following the useCodes pattern
 * @returns {Object} The mutation object with mutate function and state
 */
export function useBulkUpdateCodeColors() {
  const queryClient = useQueryClient();
  const { waitForPromise } = useOptimisticPromise();

  return useMutation({
    mutationFn: async ({ codeIds, color, source }) => {
      // Wait for all optimistic IDs to resolve to real IDs
      const resolvedCodeIds = await Promise.all(
        codeIds.map(codeId => waitForPromise(codeId))
      );

      const attributes = { color };
      if (source) {
        attributes.source = source;
      }

      await QualAPI.bulkUpdateCodes(resolvedCodeIds, attributes);
    },
    
    onMutate: async ({ codeIds, color }) => {
      // Store original data before mutation
      const originalData = {};
      
      codeIds.forEach(codeId => {
        const normalizedCodeId = normalizeId(codeId);
        const detailQueryKey = codeKeys.detail(normalizedCodeId);
        const currentCodeData = queryClient.getQueryData(detailQueryKey);
        originalData[normalizedCodeId] = currentCodeData;
        
        if (currentCodeData) {
          queryClient.setQueryData(detailQueryKey, {
            ...currentCodeData,
            color
          });
        }
      });
      
      return { originalData, codeIds };
    },
    
    onError: (_, __, context) => {
      // Rollback using context data
      const { codeIds, originalData } = context || {};
      
      if (originalData && codeIds) {
        codeIds.forEach(codeId => {
          const normalizedCodeId = normalizeId(codeId);
          const detailQueryKey = codeKeys.detail(normalizedCodeId);
          queryClient.setQueryData(detailQueryKey, originalData[normalizedCodeId]);
        });
      }
    }
  });
}


/**
 * Hook for updating code synthesis with optimistic updates
 * @returns {Object} The mutation object with mutate function and state
 */
export function useUpdateCodeSynthesis() {
  return useUpdateCodeField(
    ({ codeId, synthesis }) => QualAPI.updateCodeSynthesis(codeId, synthesis).then(response => response.data),
    'Error updating code synthesis'
  );
}

/**
 * Get code color from cache by code ID
 * @param {string} projectId - The project ID
 * @param {string} codeId - The code ID to get color for
 * @returns {string|null} The code color or null if not found
 */
export const useGetCodeColor = (projectId, codeId) => {
  const queryClient = useQueryClient();
  
  const getCodeColor = (targetCodeId) => {
    const normalizedProjectId = normalizeId(projectId);
    const normalizedCodeId = normalizeId(targetCodeId);
    
    // Try to get from detail cache first
    const codeDetail = queryClient.getQueryData(codeKeys.detail(normalizedCodeId));
    if (codeDetail?.color) {
      return codeDetail.color;
    }
    
    // Fallback to getting from list cache
    const projectCodes = queryClient.getQueryData(codeKeys.list(normalizedProjectId));
    if (projectCodes?.codes) {
      const code = projectCodes.codes.find(c => normalizeId(c.id) === normalizedCodeId);
      return code?.color || null;
    }
    
    return null;
  };
  
  return { getCodeColor };
};

/**
 * Hook for recursively changing colors of a code and its children
 */
export const useRecursiveColorChange = (projectId, codeId) => {
  const bulkUpdateColors = useBulkUpdateCodeColors();
  const childCodeIds = useChildCodeIdsFromCache(projectId, codeId);
  const { getCodeColor } = useGetCodeColor(projectId, codeId);

  const handleColorChange = async (color) => {
    const codeIds = [codeId, ...childCodeIds];
    return bulkUpdateColors.mutateAsync({ codeIds, color, source: 'user' });
  };

  const propagateColorToChildren = async (sourceCodeId) => {
    // Get color from the provided code ID
    const targetColor = getCodeColor(sourceCodeId);

    if (!targetColor) {
      console.warn('Could not find color for code:', sourceCodeId);
      return;
    }

    const codeIds = [codeId, ...childCodeIds];
    return bulkUpdateColors.mutateAsync({ codeIds, color: targetColor, source: 'inherit' });
  };

  return {
    handleColorChange,
    propagateColorToChildren,
    isLoading: bulkUpdateColors.isPending,
    isError: bulkUpdateColors.isError,
    error: bulkUpdateColors.error
  };
};

// Export all code update mutations as a group
export const codeUpdateMutations = {
  useUpdateCode,
  useUpdateCodeSynthesis,
  useBulkUpdateCodeColors
};